import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Button, Card, Wordmark } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import { ApiError, apiFetch } from '@wundo/client/api.ts';

/*
 * Where the "unsubscribe" link in a Wundo e-mail lands.
 *
 * It sits outside `_app` on purpose: whoever opens it has an inbox and nothing
 * else — no session, and often no intention of ever signing in again. The
 * signed token in the URL is the whole of what identifies them.
 *
 * Nothing happens on load. The one button is the point: mail scanners and Safe
 * Links open every address in a message before the reader does, so a page that
 * unsubscribed on arrival would unsubscribe people who never clicked.
 */
export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

type Stage = 'ready' | 'done' | 'invalid';

function UnsubscribePage() {
  const { token } = Route.useSearch();

  const [stage, setStage] = useState<Stage>(token ? 'ready' : 'invalid');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function unsubscribe() {
    if (!token) return;

    setError('');
    setBusy(true);
    try {
      await apiFetch(`/email/unsubscribe/${encodeURIComponent(token)}`, {
        method: 'POST',
      });
      setStage('done');
    } catch (caught) {
      /*
       * A refused token is a dead link and says so. Anything else is our side
       * failing, and the person can simply press the button again — telling
       * them their link is invalid would be both wrong and unfixable for them.
       */
      if (caught instanceof ApiError && caught.status === 404) {
        setStage('invalid');
      } else {
        setError(m.unsubscribe_error());
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className='mx-auto flex min-h-dvh max-w-150 flex-col gap-6 px-6 py-12'>
      <div>
        <Wordmark registered className='h-8' />
      </div>

      {stage === 'invalid' && (
        <Card>
          <h1 className='text-xl font-semibold tracking-tight'>
            {m.unsubscribe_invalid_title()}
          </h1>
          <p className='mt-3 text-base text-ink-muted'>{m.unsubscribe_invalid_body()}</p>
        </Card>
      )}

      {stage === 'done' && (
        <Card tone='accent'>
          <h1 className='text-xl font-semibold tracking-tight'>
            {m.unsubscribe_done_title()}
          </h1>
          <p className='mt-3 text-base text-ink-muted'>{m.unsubscribe_done_body()}</p>
        </Card>
      )}

      {stage === 'ready' && (
        <Card>
          <h1 className='text-xl font-semibold tracking-tight'>
            {m.unsubscribe_title()}
          </h1>
          <p className='mt-3 text-base text-ink-muted'>{m.unsubscribe_body()}</p>

          <Button
            type='button'
            variant='accent'
            shape='block'
            size='lg'
            className='mt-6'
            loading={busy}
            onClick={() => void unsubscribe()}
          >
            {m.unsubscribe_submit()}
          </Button>

          {error && (
            <p role='alert' className='mt-3 text-sm font-medium text-danger'>
              {error}
            </p>
          )}
        </Card>
      )}
    </main>
  );
}
